import React, { useState, useCallback } from 'react';
import {
  Paper, Group, Stack, Text, Title, Button, Loader, Alert, SimpleGrid, Divider, Badge
} from '@mantine/core';
import { TextInput } from '@mantine/core';
import { DataGrid } from '@mui/x-data-grid';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell
} from 'recharts';
import { IconRefresh, IconAlertCircle, IconX } from '@tabler/icons-react';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';

const DARK   = 'var(--mantine-color-dark-7)';
const BORDER = 'var(--mantine-color-dark-4)';
const TEXT   = 'var(--mantine-color-text)';

const GRID_SX = {
  border: 0,
  '--DataGrid-containerBackground': `${DARK} !important`,
  '--DataGrid-rowBackground':       `${DARK} !important`,
  '--DataGrid-footerBackground':    `${DARK} !important`,
  color: TEXT,
  bgcolor: DARK,
  '&.MuiDataGrid-root .MuiDataGrid-container--top [role=row]': {
    backgroundColor: `${DARK} !important`,
    color: `${TEXT} !important`,
  },
  '& .MuiDataGrid-columnHeaderTitle': { color: TEXT, fontWeight: 600 },
  '& .MuiDataGrid-columnSeparator':   { color: `${BORDER} !important` },
  '& .MuiDataGrid-filler':            { backgroundColor: `${DARK} !important` },
  '& .MuiDataGrid-row':               { backgroundColor: `${DARK} !important`, color: `${TEXT} !important` },
  '& .MuiDataGrid-row:hover':         { backgroundColor: 'var(--mantine-color-dark-6) !important' },
  '& .MuiDataGrid-cell':              { borderColor: BORDER, color: TEXT, backgroundColor: `${DARK} !important` },
  '& .MuiDataGrid-footerContainer':   { backgroundColor: `${DARK} !important`, borderColor: BORDER, color: TEXT },
  '& .MuiTablePagination-root':       { color: TEXT },
  '& .MuiTablePagination-selectIcon': { color: TEXT },
  '& .MuiSvgIcon-root':               { color: `${TEXT} !important` },
  '& .MuiDataGrid-overlay':           { backgroundColor: `${DARK} !important`, color: TEXT },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': { outline: 'none !important' },
};

const TOOLTIP_STYLE      = { backgroundColor: 'var(--mantine-color-body)', borderColor: 'var(--mantine-color-default-border)', color: TEXT };
const TOOLTIP_ITEM_STYLE = { color: TEXT };

const fmt    = (v) => v == null ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtInt = (v) => v == null ? '—' : Number(v).toLocaleString('en-US');
const fmtPct = (num, denom) => denom ? `${((num / denom) * 100).toFixed(1)}%` : '—';

function KpiCard({ label, value, sub }) {
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap={2} align="center">
        <Title order={3}>{value ?? '—'}</Title>
        <Text size="sm" c="dimmed" ta="center">{label}</Text>
        {sub && <Text size="xs" c="dimmed" ta="center">{sub}</Text>}
      </Stack>
    </Paper>
  );
}

const toDateStr = (d) => d.toISOString().slice(0, 10);
const defaultFrom = toDateStr(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000));
const defaultTo   = toDateStr(new Date());

const isAuto = (row) =>
  row.sales_guidance_discount_price != null && Number(row.sales_guidance_discount_price) > 0;

const DEAL_COLUMNS = [
  {
    field: 'rfq_create_date', headerName: 'RFQ Date', width: 105, sortable: false,
    renderCell: ({ value }) => value ? new Date(value).toLocaleDateString() : '—',
  },
  { field: 'deal_public_id',     headerName: 'Deal ID',   width: 150, sortable: false },
  { field: 'buyer_company_name', headerName: 'Customer',  flex: 1,    minWidth: 150, sortable: false },
  { field: 'source',             headerName: 'Source',    width: 90,  sortable: false },
  { field: 'line_count',         headerName: 'Lines',     width: 65,  sortable: false, align: 'right', headerAlign: 'right' },
  {
    field: 'has_part_in_stock', headerName: 'Actionable', width: 100, sortable: false, align: 'center', headerAlign: 'center',
    renderCell: ({ value, row }) =>
      (value || row.has_alt_part_in_stock)
        ? <Badge color="green" size="xs" variant="filled">Yes</Badge>
        : <Badge color="gray"  size="xs" variant="filled">No</Badge>,
  },
  {
    field: 'conversion_status', headerName: 'Status', width: 100, sortable: false,
    renderCell: ({ value }) => {
      const color = value === 'converted' ? 'green' : value === 'quoted' ? 'blue' : 'gray';
      return <Badge color={color} size="xs" variant="light">{value ?? '—'}</Badge>;
    },
  },
];

const PN_COLUMNS = [
  { field: 'partNumber',   headerName: 'Part Number',     flex: 1, minWidth: 130 },
  { field: 'requestCount', headerName: 'Times Requested', width: 130, align: 'right', headerAlign: 'right' },
  { field: 'lastSeen',     headerName: 'Last Seen',       width: 110,
    valueFormatter: ({ value }) => value ? new Date(value).toLocaleDateString() : '—' },
  { field: 'firstSeen',   headerName: 'First Seen',       width: 110,
    valueFormatter: ({ value }) => value ? new Date(value).toLocaleDateString() : '—' },
  { field: 'partClass',    headerName: 'Class',           width: 70 },
];

const COLUMNS = [
  {
    field: 'rfq_create_date', headerName: 'RFQ Date', width: 110, sortable: true,
    renderCell: ({ value }) => value ? new Date(value).toLocaleDateString() : '—',
  },
  { field: 'part_number',    headerName: 'Part',    width: 130, sortable: true },
  { field: 'condition_code', headerName: 'Cond',    width: 65,  sortable: true },
  { field: 'part_class',     headerName: 'Class',   width: 65,  sortable: false },
  { field: 'part_ata',       headerName: 'ATA',     width: 75,  sortable: false },
  { field: 'dw_wh',          headerName: 'WH',      width: 70,  sortable: false },
  { field: 'dw_virtual_wh',  headerName: 'Lot WH',  width: 90,  sortable: false },
  { field: 'dw_dos',         headerName: 'DOS',     width: 65,  sortable: true, align: 'right', headerAlign: 'right' },
  {
    field: 'dw_min_price', headerName: 'Min Price', width: 95, sortable: false, align: 'right', headerAlign: 'right',
    renderCell: ({ value }) => (value && Number(value) > 0) ? fmt(value) : '—',
  },
  {
    field: 'cond_price_age_days', headerName: 'Price Age', width: 85, sortable: true, align: 'right', headerAlign: 'right',
    renderCell: ({ value }) => value == null ? '—' : `${value}d`,
  },
  {
    field: 'is_price_expired', headerName: 'Exp?', width: 60, sortable: false, align: 'center', headerAlign: 'center',
    renderCell: ({ value }) => value ? <span style={{ color: 'var(--mantine-color-red-5)' }}>exp</span> : '',
  },
  {
    field: 'cond_sell_price', headerName: 'SSP', width: 100, sortable: true, align: 'right', headerAlign: 'right',
    renderCell: ({ value }) => fmt(value),
  },
  {
    field: 'fmv_price', headerName: 'FMV', width: 95, sortable: false, align: 'right', headerAlign: 'right',
    renderCell: ({ value }) => (value && Number(value) > 0) ? fmt(value) : '—',
  },
  {
    field: 'fmv_confidence', headerName: 'FMV Conf', width: 90, sortable: false, align: 'center', headerAlign: 'center',
    renderCell: ({ value }) => {
      if (!value) return '—';
      const color = value === 'HIGH' ? 'green' : value === 'MEDIUM' ? 'yellow' : 'gray';
      return <Badge color={color} size="xs" variant="filled">{value}</Badge>;
    },
  },
  {
    field: 'fmv_guidance_price', headerName: 'FMV Sim Price', width: 115, sortable: false, align: 'right', headerAlign: 'right',
    renderCell: ({ value }) => (value && Number(value) > 0) ? fmt(value) : '—',
  },
  {
    field: 'erp_quoted_unit_price', headerName: 'Salesperson Price', width: 140, sortable: false, align: 'right', headerAlign: 'right',
    renderCell: ({ value }) => fmt(value),
  },
  {
    field: '_vs_dollar', headerName: 'vs Auto $', width: 100, sortable: false, align: 'right', headerAlign: 'right',
    renderCell: ({ row }) => {
      const sp = Number(row.erp_quoted_unit_price);
      const ap = Number(row.sales_guidance_discount_price);
      if (!sp || !ap) return '—';
      const diff = sp - ap;
      const color = diff >= 0 ? 'var(--mantine-color-green-5)' : 'var(--mantine-color-red-5)';
      const abs = Math.abs(diff).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      return <span style={{ color }}>{diff >= 0 ? '+' : '-'}${abs}</span>;
    },
  },
  {
    field: '_vs_pct', headerName: 'vs Auto %', width: 90, sortable: false, align: 'right', headerAlign: 'right',
    renderCell: ({ row }) => {
      const sp = Number(row.erp_quoted_unit_price);
      const ap = Number(row.sales_guidance_discount_price);
      if (!sp || !ap) return '—';
      const pct = ((sp - ap) / ap) * 100;
      const color = pct >= 0 ? 'var(--mantine-color-green-5)' : 'var(--mantine-color-red-5)';
      return <span style={{ color }}>{pct >= 0 ? '+' : ''}{pct.toFixed(1)}%</span>;
    },
  },
  {
    field: 'is_quoted', headerName: 'Quote Created?', width: 110, sortable: false, align: 'center', headerAlign: 'center',
    renderCell: ({ value }) => value ? '✓' : '',
  },
  {
    field: 'erp_salesperson_name', headerName: 'Salesperson', width: 150, sortable: false,
    renderCell: ({ row, value }) => row.is_quoted ? (value || row.erp_salesperson || '—') : '',
  },
  { field: 'buyer_company_name', headerName: 'Customer', width: 170, sortable: false },
  {
    field: '_automatable', headerName: 'Automatable?', width: 105, sortable: false, align: 'center', headerAlign: 'center',
    renderCell: ({ row }) => isAuto(row) ? '✓' : '',
  },
  {
    field: 'dw_dos_multiplier', headerName: 'Disc %', width: 75, sortable: false, align: 'right', headerAlign: 'right',
    renderCell: ({ row }) => {
      if (!isAuto(row)) return '—';
      const m = Number(row.dw_dos_multiplier);
      return (!m || m >= 1) ? '—' : `${((1 - m) * 100).toFixed(0)}%`;
    },
  },
  {
    field: 'sales_guidance_discount_price', headerName: 'Rotabull Price', width: 120, sortable: true, align: 'right', headerAlign: 'right',
    renderCell: ({ row }) => isAuto(row) ? fmt(row.sales_guidance_discount_price) : '—',
  },
];

export default function RotabullSimulation() {
  const [from,         setFrom]        = useState(defaultFrom);
  const [to,           setTo]          = useState(defaultTo);
  const [page,         setPage]        = useState(0);
  const [pageSize,     setPageSize]    = useState(100);
  const [sortModel,    setSortModel]   = useState([{ field: 'rfq_create_date', sort: 'desc' }]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealsPage,    setDealsPage]   = useState(0);

  const buildSummaryUrl = useCallback((f = from, t = to) => {
    const p = new URLSearchParams();
    if (f) p.set('from', f);
    if (t) p.set('to',   t);
    return `${config.apiBaseUrl}/api/rotabull/simulation/summary?${p}`;
  }, [from, to]);

  const buildDealsUrl = useCallback((pg = dealsPage, f = from, t = to) => {
    const p = new URLSearchParams({ page: pg, pageSize: 25, sortField: 'rfq_create_date', sortDir: 'desc', customerCategory: 'Airline' });
    if (f) p.set('from', f);
    if (t) p.set('to',   t);
    return `${config.apiBaseUrl}/api/rotabull/opportunities?${p}`;
  }, [dealsPage, from, to]);

  const buildLinesUrl = useCallback((pg = page, ps = pageSize, sm = sortModel, f = from, t = to, did = selectedDeal?.deal_public_id) => {
    const p = new URLSearchParams({ page: pg, pageSize: ps });
    if (sm[0]) { p.set('sortField', sm[0].field); p.set('sortDir', sm[0].sort); }
    if (f)   p.set('from',   f);
    if (t)   p.set('to',     t);
    if (did) p.set('dealId', did);
    return `${config.apiBaseUrl}/api/rotabull/simulation/lines?${p}`;
  }, [page, pageSize, sortModel, from, to, selectedDeal]);

  const [summaryUrl, setSummaryUrl] = useState(() => buildSummaryUrl());
  const [dealsUrl,   setDealsUrl]   = useState(() => buildDealsUrl(0));
  const [linesUrl,   setLinesUrl]   = useState(() => buildLinesUrl(0));

  const { data: s,         isLoading: summaryLoading } = useAuthenticatedData(summaryUrl);
  const { data: dealsData, isLoading: dealsLoading   } = useAuthenticatedData(dealsUrl);
  const { data: tableData, isLoading: tableLoading, error: tableError, refetch } = useAuthenticatedData(linesUrl);

  const applyFilters = () => {
    setPage(0);
    setSelectedDeal(null);
    setSummaryUrl(buildSummaryUrl(from, to));
    setDealsUrl(buildDealsUrl(0, from, to));
    setLinesUrl(buildLinesUrl(0, pageSize, sortModel, from, to, null));
  };

  const selectDeal = (row) => {
    const next = selectedDeal?.deal_public_id === row.deal_public_id ? null : row;
    setSelectedDeal(next);
    setPage(0);
    setLinesUrl(buildLinesUrl(0, pageSize, sortModel, from, to, next?.deal_public_id));
  };

  const dealRows  = (dealsData?.rows ?? []).map((r, i) => ({ id: i, ...r }));
  const dealTotal = dealsData?.total ?? 0;
  const rows  = (tableData?.rows ?? []).map((r, i) => ({ id: i, ...r }));
  const total = tableData?.total ?? 0;
  const discountDollar = s ? (Number(s.simulatedSspRevenue || 0) - Number(s.simulatedRevenue || 0)) : null;

  const PRICE_STATUS_COLORS = { 'Valid': '#2f9e44', 'Expired': '#e03131', 'No Price': '#868e96' };
  const priceStatusData       = (s?.byPriceStatus ?? []).map(d => ({ name: d.key, value: d.cnt }));
  const priceStatusUniquePnData = (s?.byPriceStatusUniquePn ?? []).map(d => ({ name: d.key, value: d.cnt }));

  const FMV_CONF_COLORS = { 'HIGH': '#2f9e44', 'MEDIUM': '#f59f00', 'LOW': '#868e96' };
  const fmvConfData = (s?.byFmvConfidence ?? []).map(d => ({
    confidence:  d.key,
    Lines:       d.cnt,
    'FMV Revenue': Number(d.fmvRevenue),
  }));
  const combinedQuotable = Number(s?.combinedQuotable ?? 0);
  const allFmvQuotable = s ? Number(s.fmvQuotable) + Number(s.fmvLowQuotable) : 0;
  const coverageBreakdownData = s ? [
    { name: 'SSP-priced',  value: Number(s.autoQuotable) },
    { name: 'ILS FMV',     value: allFmvQuotable          },
    { name: 'Neither',     value: Math.max(0, Number(s.linesInStock) - Number(s.autoQuotable) - allFmvQuotable) },
  ] : [];
  const COVERAGE_COLORS = { 'SSP-priced': '#1971c2', 'ILS FMV': '#f59f00', 'Neither': '#495057' };

  const ilsCoverageLines = s ? Number(s.linesWithAnyHmFmv) : 0;
  const allInboundData = s ? [
    { name: 'ILS Has H/M FMV',  value: ilsCoverageLines },
    { name: 'No ILS Coverage',  value: Math.max(0, Number(s.totalLines) - ilsCoverageLines) },
  ] : [];
  const ALL_INBOUND_COLORS = {
    'ILS Has H/M FMV': '#f59f00',
    'No ILS Coverage': '#495057',
  };

  const inStockGap    = s ? Math.max(0, Number(s.linesInStock) - Number(s.autoQuotable)) : 0;
  const fmvGapData    = s ? [
    { name: 'ILS FMV (H/M)', value: Number(s.fmvQuotable) },
    { name: 'ILS FMV (Low)', value: Number(s.fmvLowQuotable) },
    { name: 'No Coverage',   value: Math.max(0, inStockGap - Number(s.fmvQuotable) - Number(s.fmvLowQuotable)) },
  ] : [];
  const FMV_GAP_COLORS  = { 'ILS FMV (H/M)': '#f59f00', 'ILS FMV (Low)': '#fd7e14', 'No Coverage': '#495057' };
  const fmvGapFillPct   = inStockGap > 0
    ? ((Number(s?.fmvQuotable) / inStockGap) * 100).toFixed(1)
    : null;

  const freqBucketData = (s?.byFreqBucket ?? []).map(d => ({
    bucket:      d.bucket,
    'Part #s':   d.partCount,
    'RFQ Lines': d.totalRequests,
  }));
  const totalFreqLines  = freqBucketData.reduce((sum, d) => sum + d['RFQ Lines'], 0);
  const repeatFreqLines = freqBucketData.filter(d => d.bucket !== '1x').reduce((sum, d) => sum + d['RFQ Lines'], 0);
  const repeatRatePct   = totalFreqLines > 0 ? ((repeatFreqLines / totalFreqLines) * 100).toFixed(1) : null;
  const topUnpricedPns  = s?.topUnpricedPns ?? [];

  const chartData = (s?.byDosBracket ?? []).map(d => ({
    bracket:         d.key,
    'Sim Price':     Number(d.simulatedRevenue),
    'Full SSP':      Number(d.sspRevenue),
    Lines:           d.cnt,
  }));

  return (
    <Stack gap="md" p="md">
      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs" align="center">
            <Title order={3}>Rotabull Simulation</Title>
            <Badge color="blue" variant="light" size="sm">Airline Customers</Badge>
          </Group>
          <Text size="xs" c="dimmed">
            Simulates automation quoting every in-stock RFQ at the DOS-adjusted price,
            using inventory and pricing data as it existed at RFQ receipt time.
          </Text>
        </Stack>
        <Button size="xs" variant="subtle" leftSection={<IconRefresh size={14} />} onClick={() => { refetch(); }}>
          Refresh
        </Button>
      </Group>

      {/* Filters */}
      <Paper withBorder p="sm" radius="md">
        <Group gap="sm" wrap="wrap">
          <TextInput type="date" label="From" size="xs" style={{ width: 150 }}
            value={from} onChange={e => setFrom(e.target.value)} />
          <TextInput type="date" label="To" size="xs" style={{ width: 150 }}
            value={to} onChange={e => setTo(e.target.value)} />
          <Button size="xs" style={{ alignSelf: 'flex-end' }} onClick={applyFilters}>
            Apply
          </Button>
        </Group>
      </Paper>

      {/* Scope Banner */}
      {s?.population && (
        <Paper withBorder p="sm" radius="md" style={{ borderColor: 'var(--mantine-color-dark-4)' }}>
          <Group gap={0} wrap="nowrap" align="stretch">
            {/* Total population */}
            <Stack gap={2} align="center" style={{ flex: 1, padding: '4px 12px' }}>
              <Text size="xs" fw={600} tt="uppercase" c="dimmed">Period Total</Text>
              <Text fw={700} size="xl">{fmtInt(s.population.allLines)}</Text>
              <Text size="xs" c="dimmed">lines · {fmtInt(s.population.allInStock)} in-stock</Text>
            </Stack>

            <div style={{ width: 1, background: 'var(--mantine-color-dark-4)', alignSelf: 'stretch' }} />

            {/* Airline scope */}
            <Stack gap={2} align="center" style={{ flex: 1, padding: '4px 12px', background: 'var(--mantine-color-blue-light)', borderRadius: 4 }}>
              <Group gap={4}>
                <Text size="xs" fw={600} tt="uppercase" c="blue">Airline (Sim Scope)</Text>
                <Badge color="blue" variant="filled" size="xs">
                  {s.population.allLines > 0 ? `${((s.population.airlineLines / s.population.allLines) * 100).toFixed(1)}%` : '—'}
                </Badge>
              </Group>
              <Text fw={700} size="xl" c="blue">{fmtInt(s.population.airlineLines)}</Text>
              <Text size="xs" c="dimmed">lines · {fmtInt(s.population.airlineInStock)} in-stock</Text>
            </Stack>

            <div style={{ width: 1, background: 'var(--mantine-color-dark-4)', alignSelf: 'stretch' }} />

            {/* Excluded */}
            <Stack gap={2} align="center" style={{ flex: 1, padding: '4px 12px' }}>
              <Group gap={4}>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed">Excluded (Non-Airline)</Text>
                <Badge color="gray" variant="light" size="xs">
                  {s.population.allLines > 0 ? `${((s.population.otherLines / s.population.allLines) * 100).toFixed(1)}%` : '—'}
                </Badge>
              </Group>
              <Text fw={700} size="xl" c="dimmed">{fmtInt(s.population.otherLines)}</Text>
              <Text size="xs" c="dimmed">lines · {fmtInt(s.population.otherInStock)} in-stock</Text>
            </Stack>
          </Group>
        </Paper>
      )}

      {/* KPI Cards */}
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
        <KpiCard
          label="Lines In"
          value={fmtInt(s?.totalLines)}
          sub={s ? `${fmtInt(s.linesInStock)} in-stock (${fmtPct(s.linesInStock, s.totalLines)})` : null}
        />
        <KpiCard
          label="Human Quoted"
          value={fmt(s?.actualQuotedRevenue)}
          sub={s ? `${fmtInt(s.humanQuotedCount)} of ${fmtInt(s.totalLines)} lines (${fmtPct(s.humanQuotedCount, s.totalLines)})` : null}
        />
        <KpiCard
          label="Could Automate"
          value={fmtInt(s?.autoQuotable)}
          sub={s ? `${fmtInt(s.autoQuotable)} of ${fmtInt(s.totalLines)} lines (${fmtPct(s.autoQuotable, s.totalLines)})` : null}
        />
        <KpiCard label="Simulated Revenue"  value={fmt(s?.simulatedRevenue)}    sub="(with discount)" />
        <KpiCard label="Full SSP Revenue"   value={fmt(s?.simulatedSspRevenue)} sub="(without discount)" />
        <KpiCard
          label="Avg Discount"
          value={s?.avgDiscountPct != null ? `${Number(s.avgDiscountPct).toFixed(1)}%` : '—'}
          sub="avg DOS discount, auto-quoted"
        />
      </SimpleGrid>

      {/* Charts */}
      {chartData.length > 0 && (
        <Group gap="md" align="flex-start">
          {/* Line count by DOS bracket */}
          <Paper withBorder p="md" radius="md" style={{ flex: 2, minWidth: 280 }}>
            <Text size="sm" fw={500} mb="xs">Auto-quotable Lines by DOS Bracket</Text>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--mantine-color-dark-4)" />
                <XAxis dataKey="bracket" tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                <Bar dataKey="Lines" fill="#2f9e44" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          {/* Price status by line count */}
          <Paper withBorder p="md" radius="md" style={{ flex: 1, minWidth: 200 }}>
            <Text size="sm" fw={500} mb="xs">Inbound Lines by Price Status</Text>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={priceStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                  dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`} labelLine={false}>
                  {priceStatusData.map((entry) => (
                    <Cell key={entry.name} fill={PRICE_STATUS_COLORS[entry.name] ?? '#868e96'} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE}
                  formatter={(v, name) => [v.toLocaleString(), name]} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>

          {/* Price status by unique part number */}
          <Paper withBorder p="md" radius="md" style={{ flex: 1, minWidth: 200 }}>
            <Text size="sm" fw={500} mb="xs">Unique Part Numbers by Price Status</Text>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={priceStatusUniquePnData} cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                  dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`} labelLine={false}>
                  {priceStatusUniquePnData.map((entry) => (
                    <Cell key={entry.name} fill={PRICE_STATUS_COLORS[entry.name] ?? '#868e96'} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE}
                  formatter={(v, name) => [v.toLocaleString(), name]} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Group>
      )}

      {tableError && (
        <Alert icon={<IconAlertCircle size={14} />} color="red" variant="light">
          Failed to load simulation lines.
        </Alert>
      )}

      {/* ── FMV Fill-In Scenario ──────────────────────────────────────────── */}
      <Divider mt="sm" />
      <Stack gap={4}>
        <Title order={5}>FMV Fill-In Scenario</Title>
        <Text size="xs" c="dimmed">
          For in-stock lines with no SSP set, simulates pricing using ILS fair market value
          calibrated by condition × confidence (HIGH/MEDIUM only). Expired prices are excluded —
          those require salesperson action.
        </Text>
      </Stack>

      <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
        <KpiCard
          label="FMV-Filled Lines"
          value={fmtInt(s?.fmvQuotable)}
          sub="unpriced lines with usable FMV"
        />
        <KpiCard label="FMV Simulated Revenue" value={fmt(s?.fmvRevenue)}    sub="(calibrated × DOS)" />
        <KpiCard
          label="Combined Quotable"
          value={fmtInt(s?.combinedQuotable)}
          sub={s ? `${fmtPct(combinedQuotable, s.totalLines)} of lines in` : null}
        />
        <KpiCard label="Combined Revenue"  value={fmt(s?.combinedRevenue)}   sub="SSP + FMV scenario" />
        <KpiCard
          label="Coverage Gain"
          value={s ? `+${fmtInt(s.fmvQuotable)}` : '—'}
          sub={s ? `+${fmt(s.fmvRevenue)} over SSP-only` : null}
        />
        <KpiCard
          label="FMV Gap Fill Rate"
          value={fmvGapFillPct != null ? `${fmvGapFillPct}%` : '—'}
          sub="of unpriced in-stock lines w/ H/M FMV"
        />
      </SimpleGrid>

      {fmvConfData.length > 0 && (
        <Group gap="md" align="flex-start">
          <Paper withBorder p="md" radius="md" style={{ flex: 1, minWidth: 220 }}>
            <Text size="sm" fw={500} mb="xs">FMV-Filled Lines by Confidence</Text>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={fmvConfData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--mantine-color-dark-4)" />
                <XAxis dataKey="confidence" tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={v => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                  formatter={(v, name) => [name === 'FMV Revenue' ? `$${Number(v).toLocaleString()}` : v, name]}
                />
                <Legend formatter={val => <span style={{ color: TEXT, fontSize: 11 }}>{val}</span>} />
                <Bar yAxisId="left"  dataKey="Lines"       fill="#f59f00" />
                <Bar yAxisId="right" dataKey="FMV Revenue" fill="#1971c2" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          <Paper withBorder p="md" radius="md" style={{ flex: 1, minWidth: 220 }}>
            <Text size="sm" fw={500} mb="xs">In-Stock Coverage Breakdown</Text>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={coverageBreakdownData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                  labelLine={false}
                >
                  {coverageBreakdownData.map((entry) => (
                    <Cell key={entry.name} fill={COVERAGE_COLORS[entry.name] ?? '#868e96'} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                  formatter={(v, name) => [v.toLocaleString(), name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>

          <Paper withBorder p="md" radius="md" style={{ flex: 1, minWidth: 220 }}>
            <Text size="sm" fw={500} mb="xs">Unpriced In-Stock — FMV Coverage</Text>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={fmvGapData}
                  cx="50%" cy="50%"
                  innerRadius={50} outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                  labelLine={false}
                >
                  {fmvGapData.map((entry) => (
                    <Cell key={entry.name} fill={FMV_GAP_COLORS[entry.name] ?? '#868e96'} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE}
                  formatter={(v, name) => [v.toLocaleString(), name]} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>

          <Paper withBorder p="md" radius="md" style={{ flex: 1, minWidth: 220 }}>
            <Text size="sm" fw={500} mb="xs">All Inbound — ILS FMV Coverage</Text>
            <Text size="xs" c="dimmed" mb="xs">Any condition, H/M confidence</Text>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={allInboundData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                  labelLine={false}
                >
                  {allInboundData.map((entry) => (
                    <Cell key={entry.name} fill={ALL_INBOUND_COLORS[entry.name] ?? '#868e96'} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                  formatter={(v, name) => [v.toLocaleString(), name]}
                />
                <Legend formatter={val => <span style={{ color: TEXT, fontSize: 10 }}>{val}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Group>
      )}

      {/* ── LOW Confidence FMV ────────────────────────────────────────────── */}
      <Divider mt="sm" />
      <Stack gap={4}>
        <Title order={5}>LOW Confidence FMV</Title>
        <Text size="xs" c="dimmed">
          Lines where the only available FMV data is LOW confidence. No calibration multiplier
          exists for this tier — revenue shown is raw FMV × DOS multiplier, likely overstated.
          Shown separately to size the opportunity before deciding whether to act on it.
        </Text>
      </Stack>

      <SimpleGrid cols={{ base: 2, sm: 2, lg: 4 }} spacing="sm">
        <KpiCard
          label="LOW Conf Lines"
          value={fmtInt(s?.fmvLowQuotable)}
          sub="unpriced in-stock, LOW FMV only"
        />
        <KpiCard
          label="LOW Conf Revenue"
          value={fmt(s?.fmvLowRevenue)}
          sub="(raw FMV × DOS, uncalibrated)"
        />
        <KpiCard
          label="Total Addressable"
          value={fmtInt(s ? Number(s.fmvQuotable) + Number(s.fmvLowQuotable) : null)}
          sub="HIGH+MEDIUM+LOW unpriced lines"
        />
        <KpiCard
          label="Total FMV Revenue"
          value={fmt(s ? Number(s.fmvRevenue) + Number(s.fmvLowRevenue) : null)}
          sub="calibrated + uncalibrated"
        />
      </SimpleGrid>

      {/* ── Demand Patterns ──────────────────────────────────────────────── */}
      <Divider mt="sm" />
      <Stack gap={4}>
        <Title order={5}>Part Number Demand Patterns</Title>
        <Text size="xs" c="dimmed">
          How often are the same part numbers requested? A concentrated distribution means the same
          parts repeat — high-frequency unpriced parts are prime targets for automated pricing.
        </Text>
      </Stack>

      {repeatRatePct != null && (
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
          <KpiCard
            label="Repeat Rate"
            value={`${repeatRatePct}%`}
            sub="of RFQ lines are PNs seen 2+ times"
          />
          <KpiCard
            label="One-Time PNs"
            value={fmtInt(freqBucketData.find(d => d.bucket === '1x')?.[`Part #s`])}
            sub="distinct PNs seen exactly once"
          />
          <KpiCard
            label="High-Freq PNs (21+)"
            value={fmtInt(freqBucketData.find(d => d.bucket === '21+')?.[`Part #s`])}
            sub="distinct PNs seen 21+ times"
          />
        </SimpleGrid>
      )}

      {freqBucketData.length > 0 && (
        <Group gap="md" align="flex-start">
          <Paper withBorder p="md" radius="md" style={{ flex: 2, minWidth: 280 }}>
            <Text size="sm" fw={500} mb="xs">PN Request Frequency Distribution</Text>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={freqBucketData} margin={{ top: 4, right: 48, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--mantine-color-dark-4)" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left"  tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                <Legend formatter={val => <span style={{ color: TEXT, fontSize: 11 }}>{val}</span>} />
                <Bar yAxisId="left"  dataKey="Part #s"   fill="#1971c2" />
                <Bar yAxisId="right" dataKey="RFQ Lines" fill="#f59f00" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Group>
      )}

      {topUnpricedPns.length > 0 && (
        <Paper withBorder p="sm" radius="md">
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={6}>
            Top Unpriced Part Numbers by Request Volume
          </Text>
          <div style={{ height: 420 }}>
            <DataGrid
              rows={topUnpricedPns.map((r, i) => ({ id: i, ...r }))}
              columns={PN_COLUMNS}
              density="compact"
              sx={GRID_SX}
              disableRowSelectionOnClick
              hideFooter
            />
          </div>
        </Paper>
      )}

      {/* ── Deals ─────────────────────────────────────────────────────────── */}
      <Divider mt="sm" />
      <Group justify="space-between" align="center">
        <Title order={5}>
          Deals{dealTotal > 0 ? ` (${fmtInt(dealTotal)})` : ''}
        </Title>
        {selectedDeal && (
          <Badge
            color="blue" variant="light" size="sm"
            rightSection={
              <IconX size={10} style={{ cursor: 'pointer' }}
                onClick={() => { setSelectedDeal(null); setPage(0); setLinesUrl(buildLinesUrl(0, pageSize, sortModel, from, to, null)); }}
              />
            }
          >
            Filtered: {selectedDeal.deal_public_id}
          </Badge>
        )}
      </Group>
      <Text size="xs" c="dimmed">Click a row to filter lines below to that deal.</Text>
      <Paper withBorder radius="md" style={{ height: 340, width: '100%', overflow: 'hidden' }}>
        {dealsLoading && <Group justify="center" p="xl"><Loader size="sm" /><Text size="sm" c="dimmed">Loading…</Text></Group>}
        {!dealsLoading && (
          <DataGrid
            rows={dealRows}
            columns={DEAL_COLUMNS}
            rowCount={dealTotal}
            paginationMode="server"
            paginationModel={{ page: dealsPage, pageSize: 25 }}
            onPaginationModelChange={({ page: p }) => {
              setDealsPage(p);
              setDealsUrl(buildDealsUrl(p, from, to));
            }}
            pageSizeOptions={[25]}
            disableRowSelectionOnClick={false}
            rowSelectionModel={selectedDeal ? [selectedDeal.id] : []}
            onRowClick={({ row }) => selectDeal(row)}
            density="compact"
            sx={{ ...GRID_SX, '& .MuiDataGrid-row': { ...GRID_SX['& .MuiDataGrid-row'], cursor: 'pointer' } }}
          />
        )}
      </Paper>

      {/* Detail Table */}
      <Group justify="space-between" align="center">
        <Title order={5}>
          {selectedDeal ? `Lines for deal ${selectedDeal.deal_public_id}` : 'Lines'}
          {total > 0 ? ` (${fmtInt(total)} in-stock)` : ''}
        </Title>
      </Group>
      <Paper withBorder radius="md" style={{ height: 600, width: '100%', overflow: 'hidden' }}>
        {tableLoading && (
          <Group justify="center" p="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Loading…</Text>
          </Group>
        )}
        {!tableLoading && (
          <DataGrid
            rows={rows}
            columns={COLUMNS}
            rowCount={total}
            paginationMode="server"
            sortingMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={({ page: p, pageSize: ps }) => {
              setPage(p);
              setPageSize(ps);
              setLinesUrl(buildLinesUrl(p, ps, sortModel, from, to));
            }}
            sortModel={sortModel}
            onSortModelChange={sm => {
              setSortModel(sm);
              setPage(0);
              setLinesUrl(buildLinesUrl(0, pageSize, sm, from, to));
            }}
            pageSizeOptions={[50, 100, 250]}
            disableRowSelectionOnClick
            density="compact"
            sx={GRID_SX}
          />
        )}
      </Paper>
    </Stack>
  );
}
